import type { CodeResponse, GroupAlias } from '@lms/api-client/backend';
import { Alert, AlertDescription, AlertTitle } from '@lms/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { InfoIcon } from 'lucide-react';
import { useLocale, useTranslations } from 'use-intl';
import {
  CODE_TRANSLATION_KEY,
  CODE_TYPE_TO_TABLE,
} from '@/features/codes/codes';
import OverflowMenu from '@/features/codes/components/code-list-overflow-menu';
import { QrCodeButton } from '@/features/codes/components/qr-code-button';
import { takeLocaleString } from '@/lib/translate';

export function CodeList({
  slug,
  groupAlias,
  hasGroups,
  codes,
  onChanged,
}: {
  slug: string;
  groupAlias: GroupAlias;
  hasGroups: boolean;
  codes: CodeResponse[];
  onChanged: () => void;
}) {
  const t = useTranslations('admin');
  const t_code = useTranslations('code');
  const locale = useLocale();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{'QR'}</TableHead>
          <TableHead>{t('code')}</TableHead>
          <TableHead>{t('name')}</TableHead>
          {hasGroups && <TableHead>{t(`${groupAlias}_singular`)}</TableHead>}
          <TableHead>{t('curriculum')}</TableHead>
          <TableHead>{t('code_uses')}</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {codes.map(
          ({
            id,
            code,
            name,
            curriculumName,
            curriculumNameTranslated,
            usageCount,
            groupName,
            codeType,
          }) => (
            <TableRow key={id}>
              <TableCell>
                <QrCodeButton slug={slug} code={code} />
              </TableCell>
              <TableCell>{code}</TableCell>
              <TableCell>{name}</TableCell>
              {hasGroups && (
                <TableCell>
                  {t_code(CODE_TRANSLATION_KEY[CODE_TYPE_TO_TABLE[codeType]], {
                    groupAlias: t(`${groupAlias}_singular`).toLowerCase(),
                    groupName: groupName ?? '',
                  })}
                </TableCell>
              )}
              <TableCell>
                {takeLocaleString(
                  locale,
                  curriculumNameTranslated ?? curriculumName,
                )}
              </TableCell>
              <TableCell>{usageCount}</TableCell>
              <TableCell>
                <OverflowMenu id={id} name={name} onChanged={onChanged} />
              </TableCell>
            </TableRow>
          ),
        )}
      </TableBody>
    </Table>
  );
}

export function EmptyCodeList({ groupAlias }: { groupAlias: GroupAlias }) {
  const t = useTranslations('admin');
  return (
    <Alert>
      <InfoIcon className="h-4 w-4" />
      <AlertTitle>{t('warning')}</AlertTitle>
      <AlertDescription>
        {t('no_groups', { what: t(groupAlias).toLowerCase() })}
      </AlertDescription>
    </Alert>
  );
}
